import type { KeyDateRef, MonthIndex, Scheduled } from '@finsim/model';

/**
 * Editing model for the schedule dialog: a list of periods where the first always starts at
 * month 0. Pure functions; the dialog keeps a draft and commits one command on save.
 */
export interface Period<T> {
  from: number;
  value: T;
  /** The key date the start is tied to. */
  anchor?: KeyDateRef | undefined;
}

export type PeriodError = 'duplicate' | 'outOfRange' | null;

/** Periods of a schedule. A constant is one period; a fallback becomes a period from month 0. */
export function toPeriods<T>(s: Scheduled<T>): Period<T>[] {
  if (s.kind === 'constant') return [{ from: 0, value: s.value }];
  const periods: Period<T>[] = s.segments.map((seg) =>
    seg.anchors?.from
      ? { from: seg.from, value: seg.value, anchor: seg.anchors.from }
      : { from: seg.from, value: seg.value },
  );
  const first = periods[0];
  if (first && first.from > 0 && s.fallback !== undefined)
    return [{ from: 0, value: s.fallback }, ...periods];
  if (first && first.from > 0) first.from = 0;
  return periods;
}

/** Back to a `Scheduled`: one period is a constant, several are a schedule (sorted). */
export function fromPeriods<T>(periods: readonly Period<T>[]): Scheduled<T> {
  const sorted = [...periods].sort((a, b) => a.from - b.from);
  const first = sorted[0];
  if (!first) throw new Error('A schedule needs at least one period');
  if (sorted.length === 1) return { kind: 'constant', value: first.value };
  return {
    kind: 'schedule',
    segments: sorted.map((p) =>
      p.anchor
        ? { from: p.from as MonthIndex, value: p.value, anchors: { from: p.anchor } }
        : { from: p.from as MonthIndex, value: p.value },
    ),
  };
}

/** Appends a period a year after the last one (clamped to the horizon), copying its value. */
export function addPeriod<T>(periods: readonly Period<T>[], horizon: number): Period<T>[] {
  const last = periods.at(-1);
  if (!last) return [...periods];
  const from = Math.min(horizon - 1, last.from + 12);
  if (from <= last.from) return [...periods];
  return [...periods, { from, value: last.value }];
}

/** Splits period `index` in two at its midpoint (both halves keep the value). */
export function splitPeriod<T>(
  periods: readonly Period<T>[],
  index: number,
  horizon: number,
): Period<T>[] {
  const current = periods[index];
  if (!current) return [...periods];
  const end = periods[index + 1]?.from ?? horizon;
  const from = current.from + Math.floor((end - current.from) / 2);
  if (from <= current.from) return [...periods];
  return [
    ...periods.slice(0, index + 1),
    { from, value: current.value },
    ...periods.slice(index + 1),
  ];
}

/** Removes a period; the first (from month 0) cannot be removed. */
export function removePeriod<T>(periods: readonly Period<T>[], index: number): Period<T>[] {
  if (index <= 0) return [...periods];
  return periods.filter((_, i) => i !== index);
}

/**
 * Changes a period's start (tied to `anchor`, if given) and keeps the list sorted; the first
 * period stays at month 0.
 */
export function movePeriod<T>(
  periods: readonly Period<T>[],
  index: number,
  from: number,
  anchor?: KeyDateRef,
): Period<T>[] {
  if (index <= 0) return [...periods];
  const next = periods.map((p, i) => {
    if (i !== index) return p;
    const { anchor: _, ...rest } = p;
    return anchor ? { ...rest, from, anchor } : { ...rest, from };
  });
  const [first, ...rest] = next;
  return first ? [first, ...rest.sort((a, b) => a.from - b.from)] : next;
}

export function setPeriodValue<T>(
  periods: readonly Period<T>[],
  index: number,
  value: T,
): Period<T>[] {
  return periods.map((p, i) => (i === index ? { ...p, value } : p));
}

/** Per-row problems: two periods starting in the same month, or a start outside the horizon. */
export function periodErrors<T>(periods: readonly Period<T>[], horizon: number): PeriodError[] {
  return periods.map((p, i) => {
    if (p.from < 0 || p.from >= horizon) return 'outOfRange';
    if (periods.some((q, j) => j !== i && q.from === p.from)) return 'duplicate';
    return null;
  });
}
